import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  QualityIssueDetailSchema,
  QualityIssueSchema,
  type EmployeeDto,
  type QualityIssueDetailDto,
} from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ActiveEmployeePicker, ListSelect, Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { listLabel, QUALITY_ISSUES, useQualityLists } from './common';

const PAGE_SIZE = 50;
type FilterKey = 'status' | 'category' | 'severity' | 'search' | 'page';

/**
 * Quality issues (FRD §18, FRD-QLT-003 to QLT-008; CLR-43): each with its number, owner, category
 * and severity where set, and how many of its actions are still open. Filters and the page are
 * server-side and kept in the URL.
 */
export function QualityIssuesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'quality.quality_issue:create');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState(params.get('search') ?? '');
  const lists = useQualityLists();
  const status = params.get('status') ?? 'open';
  const category = params.get('category') ?? '';
  const severity = params.get('severity') ?? '';
  const term = params.get('search') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: FilterKey, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' && key !== 'status') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-code',
  });
  if (status !== 'all') query.set('filter[status]', status);
  if (category !== '') query.set('filter[category]', category);
  if (severity !== '') query.set('filter[severity]', severity);
  if (term !== '') query.set('filter[search]', term);
  const issues = useQuery({
    queryKey: ['quality', 'issues', 'list', query.toString()],
    queryFn: () => getPage(`${QUALITY_ISSUES}?${query.toString()}`, QualityIssueSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Quality issues"
        description="Quality issues and non-conformances, with their owner, their corrective and preventive actions, and their verification and closure."
        actions={
          <>
            <ExportButton
              recordType="quality.quality_issue"
              href={exportHref(QUALITY_ISSUES, query)}
            />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New issue
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label="Quality issue filters"
        label="Search by number or title"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== term) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={status === 'all' ? '' : status}
          onChange={(e) => {
            set('status', e.target.value === '' ? 'all' : e.target.value);
          }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
        {(
          [
            ['category', 'Category', category, lists.data?.category],
            ['severity', 'Severity', severity, lists.data?.severity],
          ] as const
        ).map(([key, label, value, values]) => (
          <TextField
            key={key}
            select
            size="small"
            label={label}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            value={value}
            onChange={(e) => {
              set(key, e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(values ?? []).map((option) => (
              <MenuItem key={option.valueCode} value={option.valueCode}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ))}
      </SearchFilterBar>
      {issues.isError && <ErrorState message={issues.error.message} />}
      <Section>
        {issues.isPending && <LoadingState label="Loading quality issues" />}
        {issues.data?.items.length === 0 && <EmptyState title="No quality issues found" />}
        {issues.data !== undefined && issues.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Quality issues">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Identified</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Open actions</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.data.items.map((issue) => (
                  <TableRow key={issue.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/quality/issues/${issue.id}`}>{issue.code}</RecordLink>
                    </TableCell>
                    <TableCell>{issue.title}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{issue.identifiedOn}</TableCell>
                    <TableCell>
                      {issue.ownerLabel == null ? (
                        '—'
                      ) : (
                        <PersonName name={issue.ownerLabel} employeeId={issue.ownerEmployeeId} />
                      )}
                    </TableCell>
                    <TableCell>{listLabel(lists.data?.category, issue.categoryCode)}</TableCell>
                    <TableCell>{listLabel(lists.data?.severity, issue.severityCode)}</TableCell>
                    <TableCell>
                      {issue.actionCount === 0
                        ? '—'
                        : `${String(issue.openActionCount)} of ${String(issue.actionCount)}`}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={issue.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={issues.data.total}
              noun="issue(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <QualityIssueDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/quality/issues/${saved.id}`);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Record (no `issue`) or edit a quality issue. The number is generated and the raiser is fixed
 * once recorded; an edit sends only what changed, with If-Match.
 */
export function QualityIssueDialog({
  issue,
  onClose,
}: {
  issue?: QualityIssueDetailDto;
  onClose: (saved?: QualityIssueDetailDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const lists = useQualityLists();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const creating = issue === undefined;
  const [title, setTitle] = useState(issue?.title ?? '');
  const [description, setDescription] = useState(issue?.description ?? '');
  const [identifiedOn, setIdentifiedOn] = useState(
    issue?.identifiedOn ?? businessToday(session.data?.businessTimezone),
  );
  const [raiser, setRaiser] = useState<EmployeeDto | null>(null);
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const [category, setCategory] = useState(issue?.categoryCode ?? '');
  const [severity, setSeverity] = useState(issue?.severityCode ?? '');
  const [rootCause, setRootCause] = useState(issue?.rootCause ?? '');

  const body: Record<string, string | null> = {};
  if (creating) {
    body.title = title.trim();
    body.description = description.trim();
    body.identifiedOn = identifiedOn;
    if (raiser !== null) body.raisedByEmployeeId = raiser.id;
    if (owner !== null) body.ownerEmployeeId = owner.id;
    if (category !== '') body.categoryCode = category;
    if (severity !== '') body.severityCode = severity;
    if (rootCause.trim() !== '') body.rootCause = rootCause.trim();
  } else {
    if (title.trim() !== issue.title) body.title = title.trim();
    if (description.trim() !== issue.description) body.description = description.trim();
    if (identifiedOn !== issue.identifiedOn) body.identifiedOn = identifiedOn;
    if (owner !== null && owner.id !== issue.ownerEmployeeId) body.ownerEmployeeId = owner.id;
    if (category !== (issue.categoryCode ?? '')) body.categoryCode = category || null;
    if (severity !== (issue.severityCode ?? '')) body.severityCode = severity || null;
    if (rootCause.trim() !== (issue.rootCause ?? '')) body.rootCause = rootCause.trim() || null;
  }
  const complete =
    title.trim() !== '' &&
    description.trim() !== '' &&
    identifiedOn !== '' &&
    (!creating || (raiser !== null && owner !== null));

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(QUALITY_ISSUES, body, QualityIssueDetailSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${QUALITY_ISSUES}/${issue.id}`,
            body,
            QualityIssueDetailSchema,
            session.data?.csrfToken ?? '',
            issue.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['quality'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose(saved);
    },
  });

  return (
    <FormDialog
      open
      title={creating ? 'New quality issue' : `Edit ${issue.code}`}
      submitLabel={creating ? 'Record issue' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Title"
        required
        autoFocus
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
      />
      <TextField
        label="What is the issue?"
        required
        multiline
        minRows={3}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
      <TextField
        type="date"
        label="Identified on"
        required
        value={identifiedOn}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          setIdentifiedOn(e.target.value);
        }}
      />
      {canSeeEmployees ? (
        <>
          {creating && (
            <ActiveEmployeePicker
              label="Raised by"
              required
              value={raiser}
              onChange={setRaiser}
              helperText="The employee who raised the issue."
            />
          )}
          <ActiveEmployeePicker
            label={creating ? 'Owner' : 'Change owner'}
            required={creating}
            value={owner}
            onChange={setOwner}
            helperText={
              creating
                ? 'The employee responsible for the issue until it is closed.'
                : `Currently ${issue.ownerLabel ?? 'not visible'}`
            }
          />
        </>
      ) : (
        <Alert severity="warning" variant="outlined">
          {creating
            ? 'Choosing who raised the issue and its owner needs permission to view employees.'
            : 'Changing the owner needs permission to view employees.'}
        </Alert>
      )}
      <ListSelect
        label="Category"
        values={lists.data?.category}
        value={category}
        current={issue?.categoryCode}
        onChange={setCategory}
      />
      <ListSelect
        label="Severity"
        values={lists.data?.severity}
        value={severity}
        current={issue?.severityCode}
        onChange={setSeverity}
      />
      <TextField
        label="Root cause"
        multiline
        minRows={2}
        value={rootCause}
        onChange={(e) => {
          setRootCause(e.target.value);
        }}
        helperText="Optional now; it can be recorded once it is known."
      />
    </FormDialog>
  );
}
